/**
 * @fileoverview Pantalla 2 — Reanudar.
 * Aparece cuando el código ya está en estado en_progreso.
 */

import { Bookmark } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AppShell } from "../components/AppShell";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { Button } from "../components/ui/button";
import { useParticipacion } from "../context/ParticipacionContext";
import { reanudarParticipacion } from "../lib/api";
import { mensajeDeError } from "../lib/utils-app";

export function Reanudar() {
    const navigate = useNavigate();
    const { state, dispatch } = useParticipacion();
    const [reanudando, setReanudando] = useState(false);
    const [error, setError] = useState(/** @type {string | null} */ (null));

    useEffect(() => {
        if (!state.codigo) {
            navigate("/", { replace: true });
        }
    }, [state.codigo, navigate]);

    async function handleContinuar() {
        if (!state.codigo) return;
        setReanudando(true);
        setError(null);
        try {
            const res = await reanudarParticipacion(state.codigo);
            dispatch({
                type: "SET_SESION",
                payload: {
                    session_token: res.session_token,
                    participacion_id: res.participacion_id,
                    expira_en: res.expira_en,
                },
            });
            dispatch({ type: "SET_VOTOS_PREVIOS", payload: res.votos_previos });
            navigate("/votacion");
        } catch (e) {
            const code = /** @type {any} */ (e)?.code;
            if (code === "token_expirado") {
                const msg = mensajeDeError(e);
                dispatch({ type: "RESET" });
                navigate("/", { replace: true, state: { errorInicial: msg } });
                return;
            }
            setError(mensajeDeError(e));
            setReanudando(false);
        }
    }

    function handleEmpezarDeNuevo() {
        // El backend no permite reset; volvemos a inicio sin tocar nada.
        navigate("/");
    }

    return (
        <AppShell testid="page-reanudar">
            <div className="space-y-6">
                <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center">
                        <Bookmark className="text-accent" size={22} />
                    </span>
                    <div className="space-y-1.5">
                        <h1 className="font-serif-headline text-2xl sm:text-3xl font-semibold text-foreground">
                            Vimos que ya empezaste
                        </h1>
                        <p className="text-base text-muted-foreground">
                            Encontramos respuestas guardadas para el código{" "}
                            <span className="font-mono font-semibold text-foreground">
                                {state.codigo}
                            </span>
                            . ¿Quieres continuar donde te quedaste?
                        </p>
                    </div>
                </div>

                {error ? (
                    <p
                        role="alert"
                        className="text-sm text-destructive bg-destructive/10 rounded-lg p-3"
                        data-testid="reanudar-error"
                    >
                        {error}
                    </p>
                ) : null}

                <div className="flex flex-col gap-3 pt-2">
                    <Button
                        size="lg"
                        className="w-full h-12 text-base font-semibold"
                        onClick={handleContinuar}
                        disabled={reanudando}
                        data-testid="continuar-participacion-btn"
                    >
                        {reanudando ? (
                            <>
                                <LoadingSpinner size={18} className="mr-2" /> Cargando tus respuestas...
                            </>
                        ) : (
                            "Continuar mi participación"
                        )}
                    </Button>
                    <button
                        type="button"
                        onClick={handleEmpezarDeNuevo}
                        disabled={reanudando}
                        className="w-full text-sm py-3 text-muted-foreground hover:text-foreground"
                        data-testid="empezar-de-nuevo-btn"
                    >
                        Empezar de nuevo
                    </button>
                </div>
            </div>
        </AppShell>
    );
}
